import {render} from "@khan/component-tex";
import Log, {Errors} from "@khan/logging";

/**
 * Determines if the input string contains math formatting and, if so, converts
 * it to plaintext.  Minimum O(n) cost.
 *
 * @param text a string that may include math formatting.
 * @returns a plaintext string.
 */
export function scrubTex(text: string): string {
    if (text.match(texMathDelimiterRegex)) {
        return convertToPlaintext(text);
    }
    return text;
}

const texMathDelimiterRegex = /\\\(|\\\)/g;

/**
 * Reformats math formatting into plaintext representations.  This function is expensive: at least O(9n)
 *
 * @param tex a string containing math formatting.
 * @returns a (mostly) plaintext string.  There may still be some math
 * formatting for advanced math notation.
 */
function convertToPlaintext(tex: string): string {
    const {normalizedTex} = render(tex); // unknown cost - likely at least O(n)

    // remove delimiters
    let updatedText = normalizedTex.replaceAll(texMathDelimiterRegex, "");

    // replace basic math operations
    updatedText = updatedText.replaceAll("\\times", "*");
    updatedText = updatedText.replaceAll("\\div", "/");

    updatedText = reformatInequalities(updatedText); // O(4n)

    updatedText = reformatFractions(updatedText);

    return updatedText;
}

/**
 * Reformats inequality math formatting into plaintext representations.  This
 * function is expensive: O(4n)
 *
 * @param tex a string potentially containing inequality math formatting.
 * @returns a string where all inequality math formatting has been replaced with
 * reasonable plaintext.
 */
function reformatInequalities(tex: string): string {
    let updatedText = tex.replaceAll("\\ge", ">=");
    updatedText = updatedText.replaceAll("\\gt", ">");
    updatedText = updatedText.replaceAll("\\le", "<=");
    updatedText = updatedText.replaceAll("\\lt", "<");

    return updatedText;
}

// Regular expression to match the frac terms and capture the numerator and denominator
const commonFracDelim = "frac";
const fracStart: RegExp = new RegExp(`\\\\[dt]*${commonFracDelim}`); // needs 4 backslashes because we're using a string literal
const fracTerms: RegExp = /\{([^}]+)\}\{([^}]+)\}/;
const wholeFraction = new RegExp(`${fracStart.source}${fracTerms.source}`);

interface FractionParts {
    numerator: string;
    denominator: string;
}

/**
 * Reformats fraction math formatting into plaintext representations.  This
 * function is expensive: O(n) when there are no fractions and ~O(3n * m) where
 * m is the number of fractions in the string.
 *
 * @param tex a string potentially containing fraction math formatting.
 * @returns a string where all fraction math formatting has been replaced with
 * reasonable plaintext.
 */
function reformatFractions(text: string): string {
    let match: RegExpExecArray | null;

    let updatedText = text;

    let fractionSubstring: string;
    let fractionIndex: number = text.length;

    const getFractionSubstring = () => {
        fractionIndex = getIndexForLastFraction(updatedText, fractionIndex); // O(n) - parsing from the back ensures we work from the "inside" outwards when dealing with nested fractions
        return fractionIndex !== -1 ? updatedText.substring(fractionIndex) : "";
    };

    while ((fractionSubstring = getFractionSubstring())) {
        match = wholeFraction.exec(fractionSubstring);
        if (match !== null) {
            const matchTerm: FractionParts = {
                numerator: match[1],
                denominator: match[2],
            };
            // format the numerator for readability
            const num =
                matchTerm.numerator.length === 1
                    ? matchTerm.numerator
                    : `(${matchTerm.numerator})`;

            // format the denominator for readability
            const denom =
                matchTerm.denominator.length === 1
                    ? matchTerm.denominator
                    : `(${matchTerm.denominator})`;

            // perform the replacement
            const plaintextFraction = `${num} / ${denom}`;
            updatedText = updatedText.replace(match[0], plaintextFraction);
        } else {
            fractionIndex -= 1; // if the match is a bad format, don't reformat it and move on
        }
    }

    return updatedText;
}

/**
 * Function for finding the last index of fraction formatting in a string.
 * It handles default formatting as well as styled fraction formatting.
 * Its runtime complexity is O(3n).
 *
 * @param text the string to search within for the fraction formatting
 * @param position the index to begin searching at. Defaults to the index of the last character.
 * @returns the index of the start of the fraction formatting.  Returns -1 if no match was found.
 */
function getIndexForLastFraction(text: string, position?: number): number {
    let commonDelimIndex = position ? position : text.length - 1;

    while (commonDelimIndex >= 0) {
        commonDelimIndex = text.lastIndexOf(commonFracDelim, commonDelimIndex); // O(n), finding the string occurence from the back keeps us in O(n) as opposed to O(n^2) when using matchAll(regex)

        if (commonDelimIndex === -1) {
            return -1;
        }

        // we know at this point that we have a _candidate_ and we need to make sure it really is formatting
        // real fraction formatting has a '\' and might have 'd' or 't' prepended to 'frac'
        const stylePrefixPadding = 2; // padding ensures the substring we consider includes the formatting delim '\' and a possible style character
        const substrStartIndex =
            commonDelimIndex < stylePrefixPadding
                ? 0
                : commonDelimIndex - stylePrefixPadding;
        const subStrWithFrac = text.substring(substrStartIndex); // O(n)

        const match = subStrWithFrac.match(fracStart); // O(n)
        if (match && match.index !== undefined) {
            return substrStartIndex + match.index;
        }
        // Did not find a match, the common delim might have pointed us at a word -> continue
        commonDelimIndex -= 1;
    }
    return -1; // Return -1 if no match is found
}

export function writeClipboardText(text: string): Promise<void> {
    try {
        return navigator.clipboard.writeText(text); // writeText returns a promise but also can throw a NotAllowedError (which is a DOMException)
    } catch (err: unknown) {
        const message = "Failed to copy text to clipboard";
        Log.error(message, Errors.NotAllowed);
        return Promise.reject(message);
    }
}

export const exportedForTesting = {
    reformatFractions,
    getIndexForLastFraction,
};
